import { useEffect, useRef, useState } from 'react'
import { gameStatus, playedLabel, versionLabel } from '@aurora/core'
import { useAuroraStore } from '../../store/auroraStore'
import { activeChild, gameRevision, gameTitle, gameVersions } from '../../store/selectors'
import { GameMenu } from './GameMenu'
import { clock, gameFace, madeAt, makingNote, gameNoun, gameSkill, openGame, playGame, retryGame } from '../../lib/games'
import { useStepStanding } from './usePath'
import { formatRelative, relativeDay, todayISO } from '../../lib/dates'
import { useMakingElapsed } from '../../hooks/useElapsed'

/**
 * One game, wherever it is listed: the Playroom, an activity card, a reply
 * from Sapling. It says honestly where the game stands (being made, with the
 * real seconds; ready; or did not come back, with a retry), plays in one
 * tap when it can, and opens the game's own page from anywhere else on it.
 *
 * A game that finishes while it is on screen arrives: one soft glow, once,
 * so a parent who has been waiting sees the moment it is ready.
 */
export function GameRow({ artifact, blockRef, listed }: {
  artifact: any
  /** Listed on this activity's card: playing it finishes the activity, and the row need not name it. */
  blockRef?: string
  /**
   * In the Playroom's list, under a heading that already names its skill:
   * the row leaves the skill out and carries the game's menu, to tidy the list.
   */
  listed?: boolean
}) {
  const status = gameStatus(artifact)
  const revision = useAuroraStore((s: any) => (status === 'ready' ? gameRevision(s, artifact._id) : undefined))
  const plan = useAuroraStore((s: any) => (!blockRef && artifact.blockRef ? planDay(s.blocks[artifact.blockRef], activeChild(s)?.timezone) : undefined)) as string | undefined
  const elapsed = useMakingElapsed(artifact, status === 'making')
  // A change on its way ticks the same clock its page shows, so the list and the page say the same thing.
  const revisionElapsed = useMakingElapsed(revision, !!revision && gameStatus(revision) === 'making')
  const arrived = useArrival(status)
  const noun = gameNoun(artifact)
  // A reading-path step listed here (a book the plan names) is locked exactly
  // when the path shows it locked, and says what opens it the same way.
  const step = useStepStanding(artifact)
  const locked = status === 'ready' && step?.state === 'locked'

  const changes = (artifact.version ?? 1) - 1
  // One grammar for every ready row, so a list of them compares at a glance:
  // what it practises, the day's plan it came from, how often it was
  // changed, its age, and how often she has played it. Most games are new,
  // so not having been played is the quiet default and never said; a game
  // she has played says so, and that is the news in the line.
  const line: Array<{ text: string }> =
    status === 'making'
      ? [{ text: `${artifact.supersedes ? 'Making the change' : 'Making it'} · ${clock(elapsed)} · ${makingNote(elapsed)}` }]
      : status === 'failed'
        ? [{ text: artifact.generation?.error ?? 'This one did not come back.' }]
        : locked
          ? [{ text: step!.unlockHint ?? 'Not open on her reading path yet' }]
        : revision
          ? [{ text: gameStatus(revision) === 'making'
              ? sentence([`Making version ${revision.version ?? 'next'}`, clock(revisionElapsed), versionLabel(revision), 'this one plays until then'])
              : 'A change did not come back · open it to ask again' }]
          : [
              listed ? undefined : gameSkill(artifact),
              plan ? `from ${plan}` : undefined,
              changes > 0 ? `changed ${changes === 1 ? 'once' : `${changes} times`}` : undefined,
              madeAt(artifact) ? formatRelative(madeAt(artifact)!) : undefined,
            ].filter((t): t is string => !!t).map((text) => ({ text }))
  if (status === 'ready' && !revision && !locked && artifact.playCount) line.push({ text: playedLabel(artifact) })
  const parts = line.filter((p) => p.text).map((p, i) => ({ ...p, text: i === 0 ? p.text.charAt(0).toUpperCase() + p.text.slice(1) : p.text }))
  const versions = useAuroraStore((s: any) => (listed ? gameVersions(s, artifact._id) : undefined)) as any[] | undefined

  const title = useAuroraStore((s: any) => gameTitle(s, artifact)) as string

  return (
    <article className={`game is-${status}${locked ? ' is-locked' : ''}${arrived ? ' is-arrived' : ''}`}>
      <button className="game-face" onClick={() => openGame(artifact._id)} aria-label={`${title}, ${statusWord(status)}. Open its page`}>
        <span className="game-thumb" aria-hidden="true">
          {status === 'making' ? <MakingFace artifact={artifact} /> : <GameFace artifact={artifact} />}
        </span>
        <span className="game-text">
          {/* Every row here is a game; only something else (a book) says what it is. */}
          {noun !== 'Game' && <span className="game-noun">{noun}</span>}
          <span className="game-title">{title}</span>
          <span className={`game-line${(status !== 'ready' || revision) && !locked ? ' is-status' : ''}${locked ? ' is-locked' : ''} tnum`} aria-live={status === 'making' || revisionElapsed ? 'off' : 'polite'}>
            {locked && <LockIcon />}
            {parts.map((p, i) => (
              <span key={i}>{i > 0 ? ' · ' : ''}{p.text}</span>
            ))}
          </span>
        </span>
      </button>
      <span className="game-act">
        {status === 'ready' && !locked && (
          // In a list the row is what a parent reaches for; Play stays one
          // tap but quiet, so a page of games is not a wall of green.
          <button className={`btn ${listed ? 'btn-ghost' : 'btn-primary'} btn-sm game-play`} onClick={() => playGame(artifact._id, blockRef)}>
            <PlayIcon /> Play
          </button>
        )}
        {locked && (
          // Not hers to read alone yet; a parent can always read it with her, from its page, without it counting on her path.
          <button className="btn btn-ghost btn-sm game-play" onClick={() => openGame(artifact._id, { preview: true })}>
            <BookIcon /> Read together
          </button>
        )}
        {status === 'failed' && (
          <button className="btn btn-ghost btn-sm" onClick={() => retryGame(artifact)}>
            <RetryIcon /> Try again
          </button>
        )}
        {versions && status !== 'making' && <GameMenu versions={versions} compact />}
      </span>
    </article>
  )
}

/** The plan an activity's game came from, by its day: "today's plan", "Monday's plan". */
function planDay(block: any, zone?: string): string | undefined {
  if (!block?.date) return block ? 'an activity' : undefined
  const day = relativeDay(block.date, todayISO(zone))
  return `${/^(Today|Tomorrow|Yesterday)$/.test(day) ? day.toLowerCase() : day}'s plan`
}

/**
 * What a game wears inside its thumbnail (lib/games gameFace): its picture,
 * its emoji, or its first letter, never a blank mark.
 */
export function GameFace({ artifact }: { artifact: any }) {
  const name = useAuroraStore((s: any) => activeChild(s)?.name) as string | undefined
  const { picture, emoji, letter } = gameFace(artifact, name)
  if (picture) return <img key={picture} src={picture} alt="" />
  if (emoji) return <span className="game-thumb-emoji">{emoji}</span>
  return <span className="game-thumb-letter">{letter}</span>
}

/**
 * A game being made: what it will wear, when its request already names a
 * thing with a picture (an owl, a fish), faded under a light that keeps
 * moving across it; else the moving light alone. Either way it reads as
 * work going on, never as an empty square.
 */
export function MakingFace({ artifact }: { artifact: any }) {
  const name = useAuroraStore((s: any) => activeChild(s)?.name) as string | undefined
  const { picture, emoji } = gameFace(artifact, name)
  return (
    <span className="game-thumb-making">
      {picture ? <img src={picture} alt="" /> : emoji ? <span className="game-thumb-emoji">{emoji}</span> : null}
    </span>
  )
}

/** Parts joined with a middle dot, the first letter raised. */
function sentence(parts: Array<string | undefined>): string {
  const s = parts.filter(Boolean).join(' · ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const statusWord = (s: string) => (s === 'making' ? 'being made' : s === 'failed' ? 'did not come back' : 'ready to play')

/** True for a moment after a game this row was showing as making becomes ready. */
function useArrival(status: string): boolean {
  const prev = useRef(status)
  const [arrived, setArrived] = useState(false)
  useEffect(() => {
    if (prev.current === 'making' && status === 'ready') {
      setArrived(true)
      const id = window.setTimeout(() => setArrived(false), 2400)
      prev.current = status
      return () => window.clearTimeout(id)
    }
    prev.current = status
  }, [status])
  return arrived
}

const S = {
  viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.1,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true,
}
export const PlayIcon = () => (
  <svg {...S} width="13" height="13" fill="currentColor" strokeWidth={1.4}><path d="M8 5.5v13l10.5-6.5z" /></svg>
)
const LockIcon = () => (
  <svg {...S} width="12" height="12" strokeWidth={2}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
)
const BookIcon = () => (
  <svg {...S} width="13" height="13" strokeWidth={1.9}><path d="M4 19.5V5a2 2 0 0 1 2-2h14v16H6a2 2 0 0 0-2 2.5zM20 19v2H6" /></svg>
)
export const RetryIcon = () => (
  <svg {...S} width="13" height="13"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" /></svg>
)
